import { PartialMessage } from '@bufbuild/protobuf';
import {
  CustomUploadResponse,
  DragAndDropFiles,
  IStyles,
  IconWrapper,
  ImageWrapper,
  ScoreProgressBarLinear,
  SecondaryButton,
  StepsBadge,
  getFileTypeName,
  pxToRem,
  pxTovW,
  theme,
} from '@geneo2-web/shared-ui';
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Slide,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { CloudUpload } from '@protos/learning_management/lms.db_pb';
import { UploadFileResponse } from '@protos/learning_management/lms.hw.student.apis_pb';
import { ChangeEvent, DragEvent, useRef, useState } from 'react';

const styles: IStyles = {
  modal: {
    boxSizing: 'border-box',
    display: { xs: 'flex', md: 'flex' },
    alignItems: { xs: 'flex-end', md: 'center' },
  },
  root: {
    width: { xs: '90%', md: pxTovW(1000), lg: pxTovW(750) },
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: { xs: pxToRem(10), md: pxTovW(20) },
    justifyContent: 'center',
    alignItems: { xs: 'center', md: 'flex-start' },
    margin: 'auto',
    border: 'none',
    outline: 'none',
    // ...rootSx,
  },
  mainContainer: {
    boxSizing: 'border-box',
    backgroundColor: 'common.white',
    width: { xs: '95%', md: pxTovW(800), lg: pxTovW(700) },
    // height: { xs: 'max-content', md: pxTovW(845), lg: pxTovW(750) },
    maxHeight: { xs: '80vh', md: pxTovW(845), lg: pxTovW(750) },
    margin: 'auto',
    p: { xs: pxToRem(10), md: pxTovW(15) },
    borderRadius: { xs: pxToRem(20), md: pxTovW(20) },
    border: 'none',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxTovW(0) },
    // ...mainContainerSx,
  },

  fileCollector: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: { xs: pxToRem(25), md: pxTovW(14) },
  },
  fileIconsCollection: {
    width: { xs: '100%', md: pxTovW(450) },
    height: { xs: pxToRem(100), md: pxTovW(132) },
  },
  selectFiles: {
    mt: { md: pxTovW(10) },
    width: { xs: pxToRem(130), md: pxTovW(250), lg: pxTovW(208) },
    height: { xs: pxToRem(40), md: pxTovW(66) },
    backgroundColor: 'primary.main',
    '&:hover': {
      backgroundColor: 'info.main',
    },
  },
  orPartition: {
    margin: 'auto',
    width: { xs: pxToRem(285), md: pxTovW(550), lg: pxTovW(451) },
    mt: { xs: pxToRem(30), md: pxTovW(60), lg: pxTovW(30) },
    position: 'relative',
    border: { xs: '1px solid #E8E8E8', md: `${pxTovW(2)} solid #E8E8E8` },
  },
  orBox: {
    margin: 'auto',
    width: 'max-content',
    borderRadius: '50%',
    p: { xs: pxToRem(8), md: pxTovW(8) },
    backgroundColor: '#E8E8E8',
    position: 'absolute',
    left: '45%',
    top: { xs: pxToRem(-15), md: pxTovW(-25), lg: pxTovW(-17) },
  },
  fileDetailsBox: {
    width: { xs: pxToRem(232), md: pxTovW(415), lg: pxTovW(315) },
    // height: { xs: pxToRem(50), md: pxTovW(83) },
    margin: 'auto',
    border: '1px dashed #0AA34F',
    backgroundColor: '#E8FEF2',
    borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
    mt: { xs: pxToRem(15), md: pxTovW(37) },
    p: {
      xs: `${pxToRem(15)} ${pxToRem(5)}`,
      md: `${pxTovW(18)} ${pxTovW(20)}`,
    },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(6), md: pxTovW(6) },
  },
  fileDetailsGreenTickBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(10) },
  },
  fileDetails: {
    mt: { xs: pxToRem(15), md: pxTovW(30) },
  },
  loadingInfoBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(14), md: pxTovW(10) },
    width: 'max-content',
    margin: 'auto',
  },
  fileNameSizeBox: {
    display: 'flex',
    gap: { xs: pxToRem(5), md: pxTovW(14) },
    maxWidth: { xs: pxToRem(190), md: pxTovW(300) },
    boxSizing: 'border-box',
  },
  nameTypo: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    wordWrap: 'break-word',
  },
  percentageAndIconBox: {
    display: 'flex',
    gap: { xs: pxToRem(5), md: pxTovW(14) },
    alignItems: 'flex-start',
  },
  deleteFileHandler: {
    height: { xs: pxToRem(14), md: pxTovW(16) },
    width: { xs: pxToRem(14), md: pxTovW(16) },
    cursor: 'pointer',
  },
};

interface IProps {
  open: boolean;
  handleClose?: () => void;
  handleDropFunction: (event: DragEvent<HTMLDivElement>) => void;
  handleChangeFunction: (event: ChangeEvent<HTMLInputElement>) => void;
  handleDeleteFunction: (indexToDelete: number) => void;
  userUploads: CustomUploadResponse[];
  isUploading?: boolean;
}

export const OmrUploadPopup = (props: IProps) => {
  const {
    open,
    handleClose,
    handleDropFunction,
    handleChangeFunction,
    handleDeleteFunction,
    userUploads,
    isUploading = false,
  } = props;

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isMedium = useMediaQuery(theme.breakpoints.down('lg'));
  const fileInfoDetails = [
    'File Format: excel',
    'File size - Minimum 10KB & Maximum 50MB each',
  ];

  const isFileUploading = isUploading;

  const inputRef = useRef<HTMLInputElement | null>(null);

  const countFileUploadSuccess = userUploads
    .filter((data) => !data.uploadFileData.isDeleted)
    .filter((upload) => upload.fileUploadStatus === 'success').length;

  const totalNonDeletedFiles = userUploads.filter(
    (data) => !data.uploadFileData.isDeleted
  ).length;

  const isAnyFilePending = userUploads?.some(
    (item) => item.fileUploadStatus === 'pending'
  );

  const hasUploadedFile = countFileUploadSuccess > 0;

  const isSelectFileDisabled =
    isAnyFilePending || isUploading || hasUploadedFile;

  const tempFileUploadPercentage = 0;

  // Type guard
  function isUploadFileResponse(
    data: PartialMessage<UploadFileResponse> | PartialMessage<CloudUpload>
  ): data is PartialMessage<UploadFileResponse> {
    return 'originalImagePath' in data;
  }

  // Type guard
  function isCloudUpload(
    data: PartialMessage<UploadFileResponse> | PartialMessage<CloudUpload>
  ): data is PartialMessage<CloudUpload> {
    return 'fileName' in data;
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isAnyFilePending) return;
    await handleDropFunction(event);
  };

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    await handleChangeFunction(event);
  };

  const handleDelete = async (indexToDelete: number) => {
    await handleDeleteFunction(indexToDelete);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <Modal open={open} onClose={handleClose && handleClose} sx={styles.modal}>
      <Slide direction="up" in={open} mountOnEnter unmountOnExit timeout={500}>
        <Box sx={styles.root}>
          <Box
            sx={{
              ...styles.mainContainer,
              cursor: isAnyFilePending ? 'wait' : 'default',
            }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <input
              type="file"
              hidden
              ref={inputRef}
              accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleChange}
            />

            <Box sx={styles.fileCollector}>
              <ImageWrapper
                // onClick={() => {  }}
                name="file-icons-collection"
                type="png"
                parentFolder="images"
                styles={styles.fileIconsCollection}
              />

              <SecondaryButton
                onClick={() => {
                  if (inputRef.current) inputRef.current.click();
                }}
                disabled={isSelectFileDisabled}
                styles={styles.selectFiles}
              >
                <Typography
                  variant={isMobile ? 'h3' : isMedium ? 'h5' : 'h3'}
                  fontWeight={'medium'}
                  color="common.white"
                >
                  SELECT FILE
                </Typography>
              </SecondaryButton>
            </Box>

            {totalNonDeletedFiles <= 0 && (
              <>
                <Box sx={styles.orPartition}>
                  <Box sx={styles.orBox}>
                    <Typography variant={'h4'} fontWeight={'medium'}>
                      OR
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: { xs: pxToRem(30), md: pxTovW(50) } }}>
                  <Typography
                    variant={'h3'}
                    fontWeight={'medium'}
                    textAlign={'center'}
                  >
                    Drop File Here
                  </Typography>

                  <Box sx={styles.fileDetailsBox}>
                    {fileInfoDetails.map((data, index) => (
                      <Box key={index} sx={styles.fileDetailsGreenTickBox}>
                        <IconWrapper
                          name="green-tick"
                          size="small"
                          parentFolder="icons"
                          type="png"
                        />

                        <Typography variant={'h4'} fontWeight={'regular'}>
                          {data}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </>
            )}

            {userUploads && userUploads.length > 0 && (
              <Box sx={styles.fileDetails}>
                <Box sx={styles.loadingInfoBox}>
                  {isFileUploading && (
                    <CircularProgress
                      color="primary"
                      size={isMobile ? pxToRem(15) : pxTovW(24)}
                    />
                  )}

                  {totalNonDeletedFiles > 0 && (
                    <Typography variant="h4" color="info.main">
                      {`${countFileUploadSuccess} out of ${totalNonDeletedFiles} uploaded`}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mt: { md: pxTovW(37) } }}>
                  {userUploads.map((file, index) => {
                    if (file.uploadFileData.isDeleted === true) return null;
                    else
                      return (
                        <Box
                          key={index}
                          sx={{
                            pt: {
                              xs: pxToRem(14),
                              md: index !== 0 ? pxTovW(17) : pxTovW(0),
                            },
                            pb: {
                              xs:
                                index !== userUploads.length - 1
                                  ? pxToRem(14)
                                  : pxToRem(0),
                              md:
                                index !== userUploads.length - 1
                                  ? pxTovW(17)
                                  : pxTovW(0),
                            },
                            borderBottom:
                              index !== userUploads.length - 1
                                ? '1px solid #D5D5D5'
                                : 'none',

                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: { xs: pxToRem(10), md: pxTovW(20) },
                            alignItems: 'center',
                            // opacity: dragOverItemIndex === index ? 0.2 : 1,
                          }}
                        >
                          {/* <Button
                          draggable={true}
                          onDragStart={() => {
                            reorderDragStart && reorderDragStart(index);
                          }}
                          onDragEnter={() => {
                            reorderDragEnter && reorderDragEnter(index);
                          }}
                          onDragEnd={() => {
                            reorderDrop && reorderDrop();
                          }}
                          disabled={isAnyFilePending}
                          sx={{ cursor: 'grab' }}
                        >
                          <IconWrapper
                            name="reorder"
                            size="small"
                            type="png"
                            parentFolder="icons"
                          />
                        </Button> */}

                          <Box sx={{ flexGrow: '1' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: { xs: pxToRem(10), md: pxTovW(10) },
                              }}
                            >
                              <Box sx={styles.fileNameSizeBox}>
                                <IconWrapper
                                  name={getFileTypeName(
                                    file.uploadFileData.fileType
                                  )}
                                  size="md"
                                  parentFolder="icons"
                                  type="png"
                                />
                                <Typography
                                  variant="h5"
                                  color="primary.main"
                                  sx={styles.nameTypo}
                                >
                                  {isUploadFileResponse(file.uploadFileData)
                                    ? file.uploadFileData.originalImagePath
                                    : isCloudUpload(file.uploadFileData)
                                    ? file.uploadFileData.fileName
                                    : ''}
                                </Typography>

                                {/* <Typography
                             variant="h5"
                             color="neutral.grey"
                             sx={{ whiteSpace: 'nowrap' }}
                           >
                             {formatFileSize(file.fileSize)}
                           </Typography> */}
                              </Box>

                              <Box sx={styles.percentageAndIconBox}>
                                <Typography
                                  variant="h5"
                                  color="neutral.grey"
                                  sx={{ whiteSpace: 'nowrap' }}
                                >
                                  {formatFileSize(file.fileSize)}
                                </Typography>
                                <Typography
                                  variant="h5"
                                  textAlign="center"
                                  color={
                                    file.fileUploadStatus === 'pending' ||
                                    file.fileUploadStatus === 'success'
                                      ? 'primary.main'
                                      : 'neutral.crimson'
                                  }
                                >
                                  {/* //todo: Get percentage of the uploaded file % */}
                                  {file.fileUploadStatus === 'success'
                                    ? '100%'
                                    : file.fileUploadStatus === 'pending'
                                    ? tempFileUploadPercentage + '%'
                                    : file.fileUploadStatus}
                                </Typography>

                                {/* if preview is available for the file */}
                                {/* <IconWrapper
                             name="gray-eye"
                             size="small"
                             parentFolder="icons"
                             type="png"
                             onClick={() => {
                               console.log('gray-eye: ', index);
                             }}
                             customSx={{
                               width: { xs: pxToRem(14), md: pxTovW(25) },
                               height: { xs: pxToRem(14), md: pxTovW(18) },
                               cursor: 'pointer',
                             }}
                           /> */}

                                {/*//^ Green tick OR Error Symbol */}
                                {file.fileUploadStatus !== 'pending' && (
                                  <IconWrapper
                                    name={
                                      file.fileUploadStatus === 'success'
                                        ? 'green-circle-check'
                                        : 'info_red'
                                    }
                                    size="md"
                                    parentFolder="icons"
                                    type="png"
                                  />
                                )}

                                {file.fileUploadStatus !== 'pending' && (
                                  <IconWrapper
                                    name="white_x_in_gray"
                                    size="md"
                                    parentFolder="icons"
                                    type="png"
                                    onClick={() => handleDelete(index)}
                                    customSx={styles.deleteFileHandler}
                                  />
                                )}
                              </Box>
                            </Box>

                            {/* //todo: Get percentage of the uploaded file % */}
                            <ScoreProgressBarLinear
                              score={
                                file.fileUploadStatus === 'success'
                                  ? 100
                                  : file.fileUploadStatus === 'pending'
                                  ? tempFileUploadPercentage
                                  : 0
                              }
                              variant="small"
                              customSx={{
                                mt: { xs: pxToRem(10), md: pxTovW(11) },
                                backgroundColor:
                                  file.fileUploadStatus === 'pending' ||
                                  file.fileUploadStatus === 'success'
                                    ? '#CBF0DC'
                                    : 'neutral.lightPink',
                              }}
                            />
                          </Box>
                        </Box>
                      );
                  })}
                </Box>
              </Box>
            )}
          </Box>
          <Box>
            <Button onClick={handleClose} sx={{ p: '0px' }}>
              <IconWrapper
                name="close-yellow"
                size="md"
                parentFolder="icons"
                type="png"
                customSx={{
                  width: { xs: pxToRem(33) },
                  height: { xs: pxToRem(33) },
                }}
              />
            </Button>
          </Box>
        </Box>
      </Slide>
    </Modal>
  );
};
